/* eslint-disable no-unused-vars */
// lingland-frontend/src/pages/AdminClientDetailsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { User, Mail, Hash, BookOpen, Clock, DollarSign, Languages, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const statusTranslations = {
    pending: 'Oczekujące', auto_quoted: 'Wycenione auto.', manual_quote_required: 'Wycena ręczna',
    quoted: 'Wycenione', accepted: 'Zaakceptowane', rejected: 'Odrzucone',
    in_progress: 'W realizacji', completed: 'Zakończone', cancelled: 'Anulowane',
};
const getStatusClass = (status) => `status-${status?.toLowerCase().replace(/\s+/g, '-').replace(/_/, '-') || 'unknown'}`;

function AdminClientDetailsPage() {
    const { email: encodedEmail } = useParams();
    const clientEmail = decodeURIComponent(encodedEmail);
    const navigate = useNavigate();
    
    const [clientData, setClientData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const token = localStorage.getItem('adminToken');

    const fetchClientDetails = useCallback(async () => {
        setIsLoading(true);
        setError('');
        if (!token) {
            setError('Brak autoryzacji.');
            setIsLoading(false);
            navigate('/admin/login');
            return;
        }

        try {
            const response = await axios.get(`${apiUrl}/api/admin/clients/${encodedEmail}`, {
                headers: { 'x-auth-token': token }
            });
            setClientData(response.data);
        } catch (err) {
            setError(err.response?.data?.errors?.[0]?.msg || 'Nie udało się pobrać szczegółów klienta.');
            if (err.response?.status === 401 || err.response?.status === 403) {
                navigate('/admin/login');
            }
        } finally {
            setIsLoading(false);
        }
    }, [apiUrl, token, navigate, encodedEmail]);

    useEffect(() => {
        fetchClientDetails();
    }, [fetchClientDetails]);

    if (isLoading) return <div className="container" style={{ paddingTop: '2rem' }}><Loader2 className="spinner"/> Ładowanie danych klienta...</div>;
    if (error) return <div className="container submit-message error" style={{ paddingTop: '2rem' }}><AlertCircle /> {error}</div>;
    if (!clientData) return <div className="container" style={{ paddingTop: '2rem' }}>Nie znaleziono danych klienta.</div>;

    return (
        <section className="admin-page section-bg-white" style={{ padding: "40px 0", minHeight: 'calc(100vh - var(--header-height) - var(--footer-height, 150px))' }}>
            <div className="container">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h1 style={{fontSize: '2rem'}}>Historia Klienta</h1>
                    <Link to="/admin/clients" className="button-secondary button-small-outline">← Wróć do listy klientów</Link>
                </div>

                <div className="client-details-card" style={{ background: 'var(--color-bg-light-gray)', padding: '1.5rem', borderRadius: 'var(--border-radius)', marginBottom: '2.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                        <User size={24} color="var(--color-accent)" />
                        <h2 style={{ margin: 0, fontSize: '1.5rem' }}>{clientData.name || 'Klient Indywidualny'}</h2>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Mail size={16} color="var(--color-text-light)" />
                        <a href={`mailto:${clientData.email}`} style={{ color: 'var(--color-text)' }}>{clientData.email}</a>
                    </div>
                </div>

                <h3>Zlecenia ({clientData.quotes.length})</h3>

                {clientData.quotes.length > 0 ? (
                    <div className="table-responsive-wrapper">
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Usługa</th>
                                    <th>Języki</th>
                                    <th>Status</th>
                                    <th>Koszt Finalny</th>
                                    <th>Data Złożenia</th>
                                    <th>Akcje</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clientData.quotes.map(quote => (
                                    <tr key={quote.id}>
                                        <td><Hash size={12} style={{verticalAlign: 'middle'}}/> {quote.id}</td>
                                        <td><BookOpen size={14} style={{verticalAlign: 'middle', marginRight: '4px'}}/> {quote.service_type}</td>
                                        <td><Languages size={14} style={{verticalAlign: 'middle', marginRight: '4px'}}/> {quote.source_language?.toUpperCase()} → {quote.target_language?.toUpperCase()}</td>
                                        <td><span className={`status-badge ${getStatusClass(quote.status)}`}>{statusTranslations[quote.status] || quote.status}</span></td>
                                        <td><DollarSign size={12} style={{verticalAlign: 'middle'}}/> {quote.final_cost ? `${quote.final_cost} PLN` : '-'}</td>
                                        <td><Clock size={12} style={{verticalAlign: 'middle'}}/> {new Date(quote.created_at).toLocaleDateString('pl-PL')}</td>
                                        <td>
                                            <Link to={`/admin/quotes/${quote.id}`} className="button-small-outline">
                                                Szczegóły
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p>Ten klient nie złożył jeszcze żadnego zapytania o wycenę.</p>
                )}
            </div>
        </section>
    );
}

export default AdminClientDetailsPage;